import React, { useEffect, useMemo, useState } from 'react';
import { TrashCanOutlined } from '@duxton-web/icons';
import {
  Button,
  ConfigProvider,
  Select,
  Tag,
  Typography,
} from '@duxton-web/ui';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import {
  Dimension,
  DimensionFilterConstraint,
  DraftDimensionFilterConstraint,
  getRestrictByConstraint,
  isViewMode,
  PopulationTargetMode,
  PopulationTargetType,
} from '../../constants';
import { useExperimentContext } from '../../contexts/experiment-context';
import {
  getDimensionFilterValueType,
  getDimensionValueInputMode,
  isDimensionFilterValueEmpty,
  isFreeTextFilterPendingValidation,
} from '../../utils/dimension-filter';
import { FreeTextMetricFilterValue } from './free-text-filter-value';
import { AndTag } from './metric-row';
import GenericFuseSelect from '@/common/selects/GenericFuseSelect/GenericFuseSelect';
import Service from '@/lib/App';
import { Operators } from '@/lib/Configurations';
import { transformPopulationTargetValueToId } from '@/utils/experiment';
import { error } from '@/utils/logging';

const LARGE_LIST_THRESHOLD = 100;

// dimension values are stored as string in the backend,
// but dimensionValues value can be string or number
// so we need to normalize dimension values so that the select component can display the correctly
const normalizedValues = (values: any, dimensionValues) => {
  if (!Array.isArray(values)) {
    return values;
  }

  if (dimensionValues.length && typeof dimensionValues[0].value === 'number') {
    return values.map((value) => +value);
  }

  return values;
};

export const MetricFilter = ({
  metricName,
  metricVersion,
  constraint,
  dimensions,
  onDelete,
  onUpdate,
  showValidation,
  isNonEditable = false,
}: {
  metricName: string;
  metricVersion?: number;
  constraint: DimensionFilterConstraint | DraftDimensionFilterConstraint;
  dimensions: Dimension[];
  onDelete: () => void;
  onUpdate: (constraint: DraftDimensionFilterConstraint) => void;
  showValidation: boolean;
  /** When true, this filter is from population constraints and cannot be edited or removed */
  isNonEditable?: boolean;
}) => {
  const params = useParams<{ action: string }>();
  const { action } = params;
  const readOnly = isViewMode(action);

  const experimentContext = useExperimentContext();
  const { experiment } = experimentContext;
  const staticGroup = experiment?.slotting?.byGroup;
  const restrictByConstraint = getRestrictByConstraint(experiment);
  const populationTargetType = restrictByConstraint?.target;
  let populationTargetValue: string | string[] =
    restrictByConstraint?.value || '';
  const populationTargetMode = staticGroup
    ? PopulationTargetMode.STATIC
    : PopulationTargetMode.DYNAMIC;

  if (populationTargetType === PopulationTargetType.CITY) {
    // check if cityValues is a stringified array of string
    if (
      typeof populationTargetValue === 'string' &&
      populationTargetValue.startsWith('[') &&
      populationTargetValue.endsWith(']')
    ) {
      populationTargetValue = populationTargetValue
        .substring(1, populationTargetValue.length - 1)
        .split(',');
    }
  }

  const [dimensionValues, setDimensionValues] = useState<
    { value: string | boolean | number; display_value: string }[]
  >([]);
  const [fetchingDimensionValues, setFetchingDimensionValues] = useState(false);
  // Omit `dimensions` from the dependency array because MetricFilter will be unmounted when the `dimensions` change
  const dimension = useMemo(() => {
    if (!constraint.dimension_name) {
      return null;
    }

    const dimension = dimensions?.find(
      (d) => d.name === constraint.dimension_name,
    );
    if (!dimension) {
      // In read-only mode, create a synthetic dimension from constraint data
      // This handles control metrics with pre-defined filters where dimensions aren't fetched
      if (readOnly) {
        return {
          name: constraint.dimension_name,
          display_name: constraint.dimension_name,
          data_type: (constraint.type as Dimension['data_type']) || 'STRING',
          cardinality: 'LOW' as const,
          governed_dimension_name: constraint.dimension_name,
        };
      }
      error(
        `Unexpected branch - Dimension ${constraint.dimension_name} not found`,
      );
      return null;
    }

    return dimension;
  }, [constraint.dimension_name, readOnly, constraint.type]);

  const valueInputMode = getDimensionValueInputMode(dimension);
  const isFreeTextValueInput = valueInputMode === 'FREE_TEXT';
  const governedValueType = isFreeTextValueInput
    ? undefined
    : getDimensionFilterValueType(dimension);

  useEffect(() => {
    if (
      !readOnly &&
      dimension &&
      governedValueType &&
      constraint.type !== governedValueType
    ) {
      onUpdate({
        ...constraint,
        type: governedValueType,
      });
    }
  }, [constraint, dimension, governedValueType, onUpdate, readOnly]);

  // Fetch filtered dimension values based on population filters
  useEffect(() => {
    if (!dimension) {
      return;
    }

    // In read-only mode, use existing constraint values instead of fetching
    // This handles control metrics with pre-defined filters
    if (readOnly && constraint.values) {
      const existingValues = Array.isArray(constraint.values)
        ? constraint.values
        : [constraint.values];
      setDimensionValues(
        existingValues.map((v) => ({
          value: v,
          display_value: String(v),
        })),
      );
      return;
    }

    if (!populationTargetMode) {
      return;
    }

    if (isFreeTextValueInput) {
      setDimensionValues([]);
      return;
    }

    if (dimension.data_type === 'BOOLEAN') {
      const booleanValues = [
        { value: true, display_value: 'True' },
        { value: false, display_value: 'False' },
      ];
      setDimensionValues(booleanValues);
      return;
    }

    let cityIds: string | null = null;
    let countryIds: string | null = null;

    const transformedPopulationTargetValue = transformPopulationTargetValueToId(
      populationTargetValue,
    );

    if (
      populationTargetMode === PopulationTargetMode.DYNAMIC &&
      populationTargetType === PopulationTargetType.CITY &&
      !!populationTargetValue
    ) {
      cityIds = transformedPopulationTargetValue;
      if (!!populationTargetValue && !transformedPopulationTargetValue) {
        cityIds = populationTargetValue as string;
      }
    }

    if (
      populationTargetMode === PopulationTargetMode.DYNAMIC &&
      populationTargetType === PopulationTargetType.COUNTRY &&
      !!populationTargetValue
    ) {
      countryIds = transformedPopulationTargetValue;
      if (!!populationTargetValue && !transformedPopulationTargetValue) {
        countryIds = populationTargetValue as string;
      }
    }

    setFetchingDimensionValues(true);

    Service.planner
      .fetchDimensionValues(metricName, constraint.dimension_name, {
        cityIds,
        countryIds,
      })
      .then((values) => {
        setDimensionValues(values.dimension_values);
      })
      .finally(() => {
        setFetchingDimensionValues(false);
      });
  }, [
    dimension,
    constraint.dimension_name,
    constraint.values,
    populationTargetMode,
    populationTargetType,
    JSON.stringify(populationTargetValue),
    metricName,
    isFreeTextValueInput,
    readOnly,
  ]);

  const combinedDimensionValues = dimensionValues;

  const shouldUseFuseSelect = dimensionValues.length > LARGE_LIST_THRESHOLD;

  const handleFieldChange = (
    updatedConstraint: Partial<DraftDimensionFilterConstraint>,
  ) => {
    if (
      Object.prototype.hasOwnProperty.call(updatedConstraint, 'dimension_name')
    ) {
      const nextDimension = dimensions?.find(
        (d) => d.name === updatedConstraint.dimension_name,
      );
      const nextValueInputMode = getDimensionValueInputMode(nextDimension);
      const nextConstraint: DraftDimensionFilterConstraint = {
        dimension_name: updatedConstraint.dimension_name,
        values: null,
        operator: null,
        type: getDimensionFilterValueType(nextDimension),
      };

      if (nextValueInputMode === 'FREE_TEXT') {
        nextConstraint.value_input_mode = nextValueInputMode;
      }

      onUpdate(nextConstraint);
      return;
    }

    if (Object.prototype.hasOwnProperty.call(updatedConstraint, 'operator')) {
      onUpdate({
        ...constraint,
        ...updatedConstraint,
        values: null,
        type: governedValueType,
        validation_status: undefined,
      });
      return;
    }

    onUpdate({ ...constraint, ...updatedConstraint });
  };

  const isValueEmpty = isDimensionFilterValueEmpty(constraint.values);

  const valueDisabled =
    readOnly || fetchingDimensionValues || !constraint.operator;
  const isFreeTextValuePendingValidation =
    isFreeTextFilterPendingValidation(constraint);
  const valueStatus =
    showValidation && (isValueEmpty || isFreeTextValuePendingValidation)
      ? 'error'
      : undefined;
  const isMultipleMode = [Operators.in, Operators['not in']].includes(
    constraint.operator,
  );
  const normalizedConstraintValues = normalizedValues(
    constraint.values,
    combinedDimensionValues,
  );
  const dimensionSelectValue = isMultipleMode
    ? Array.isArray(normalizedConstraintValues)
      ? normalizedConstraintValues
      : normalizedConstraintValues == null
        ? []
        : [normalizedConstraintValues]
    : normalizedConstraintValues;

  const renderDimensionOptionLabel = (dimension: Dimension) => {
    const isUngovernedDimension =
      getDimensionValueInputMode(dimension) === 'FREE_TEXT';

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Typography.Text>{dimension.display_name}</Typography.Text>
        {isUngovernedDimension && (
          <Tag color="orange" size="xsmall" style={{ margin: 0 }}>
            Ungoverned
          </Tag>
        )}
      </div>
    );
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
        <AndTag>AND</AndTag>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Select
            style={{ minWidth: 200 }}
            showSearch
            onChange={(value) =>
              handleFieldChange({
                dimension_name: value,
              })
            }
            placeholder="Select filter"
            value={constraint.dimension_name}
            optionFilterProp="searchLabel"
            optionLabelProp="selectedLabel"
            options={(dimensions || []).map((dimension) => ({
              key: dimension.name,
              value: dimension.name,
              searchLabel: dimension.display_name,
              selectedLabel: dimension.display_name,
              label: renderDimensionOptionLabel(dimension),
            }))}
            disabled={readOnly || isNonEditable}
          />
          <Select
            showSearch
            onChange={(value) =>
              handleFieldChange({
                operator: value,
                values: null,
              })
            }
            placeholder="Select operator"
            value={constraint.operator}
            options={[Operators['in'], Operators['not in']].map((operator) => ({
              key: operator,
              value: operator,
              label: operator,
            }))}
            style={{ width: 'fit-content' }}
            popupMatchSelectWidth={false}
            status={
              showValidation && !constraint.operator ? 'error' : undefined
            }
            disabled={readOnly || isNonEditable}
          />
          <div style={{ flexGrow: 1 }}>
            {isFreeTextValueInput ? (
              <FreeTextMetricFilterValue
                metricName={metricName}
                metricVersion={metricVersion}
                dimension={dimension}
                constraint={constraint}
                readOnly={readOnly || isNonEditable}
                valueStatus={valueStatus}
                onUpdate={onUpdate}
              />
            ) : shouldUseFuseSelect && !readOnly && !isNonEditable ? (
              <GenericFuseSelect
                data-testid="dimension-values-select"
                items={combinedDimensionValues}
                value={dimensionSelectValue}
                onChange={(newValue) => {
                  handleFieldChange({
                    values: newValue || null,
                  });
                }}
                mode={isMultipleMode ? 'multiple' : null}
                disabled={fetchingDimensionValues || !constraint.operator}
                keyFields={{
                  valueKey: 'value',
                  displayKey: 'display_value',
                }}
                status={valueStatus}
                style={{ minWidth: 300 }}
              />
            ) : (
              <Select
                data-testid="dimension-values-select"
                mode={isMultipleMode ? 'multiple' : undefined}
                style={{ minWidth: 300 }}
                showSearch
                onChange={(value) =>
                  handleFieldChange({
                    values: value || null,
                  })
                }
                placeholder="Select value"
                value={dimensionSelectValue}
                disabled={valueDisabled || isNonEditable}
                loading={fetchingDimensionValues}
                options={combinedDimensionValues.map((item) => ({
                  key: item.value,
                  value: item.value,
                  label: item.display_value,
                }))}
                status={valueStatus}
              />
            )}
          </div>
        </div>
        {!readOnly && !isNonEditable && (
          <ConfigProvider
            theme={{
              token: {
                fontWeightStrong: 400,
              },
              components: {
                Button: {
                  onlyIconSize: 14,
                  defaultBorderColor: '#bfbfbf',
                },
              },
            }}
          >
            <Button
              data-testid="filter-delete"
              shape="default"
              style={{
                flex: '0 0 40px',
                alignSelf: 'flex-start',
              }}
              icon={<TrashCanOutlined />}
              onClick={onDelete}
            />
          </ConfigProvider>
        )}
      </div>
      {showValidation &&
        (!constraint.dimension_name ||
          !constraint.operator ||
          isValueEmpty ||
          isFreeTextValuePendingValidation) && (
          <ValidationText type="danger" style={{ marginLeft: 48 }}>
            {isFreeTextValuePendingValidation
              ? 'Please save filter values'
              : 'Please complete all filter fields'}
          </ValidationText>
        )}
    </>
  );
};

const ValidationText = styled(Typography.Text)`
  font-size: 12px;
`;
